import { useEffect, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import TWEEN from 'tween';
import { useClickStore } from '../../store/clickStore';

const _matrix = new THREE.Matrix4();
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _box = new THREE.Box3();
const _center = new THREE.Vector3();
const _size = new THREE.Vector3();

function hasPositionAttribute(geometry) {
  return !!(geometry && typeof geometry.getAttribute === 'function' && geometry.getAttribute('position'));
}

function previewFromPick(object, instanceId) {
  if (!object) return null;

  const material = new THREE.MeshLambertMaterial({
    color: 0xd4d4d8,
    emissive: 0x3f3f46,
  });

  if (object.isInstancedMesh && Number.isInteger(instanceId) && instanceId >= 0 && instanceId < (object.count || 0)) {
    const geometry = hasPositionAttribute(object.geometry)
      ? object.geometry.clone()
      : new THREE.CylinderGeometry(0.15, 0.15, 1, 8);
    const mesh = new THREE.Mesh(geometry, material);
    object.getMatrixAt(instanceId, _matrix);
    _matrix.decompose(_position, _quaternion, _scale);
    mesh.position.copy(_position);
    mesh.quaternion.copy(_quaternion);
    mesh.scale.copy(_scale);
    return mesh;
  }

  if (object.isMesh && hasPositionAttribute(object.geometry) && !object.isInstancedMesh) {
    const mesh = new THREE.Mesh(object.geometry.clone(), material);
    object.updateWorldMatrix(true, false);
    object.matrixWorld.decompose(_position, _quaternion, _scale);
    mesh.position.copy(_position);
    mesh.quaternion.copy(_quaternion);
    mesh.scale.copy(_scale);
    return mesh;
  }

  const fallback = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), material);
  object.updateWorldMatrix?.(true, false);
  if (object.matrixWorld) {
    object.matrixWorld.decompose(_position, _quaternion, _scale);
    fallback.position.copy(_position);
  }
  return fallback;
}

function fitCameraToObject(camera, controls, object) {
  _box.makeEmpty();
  object.updateWorldMatrix(true, true);
  object.traverse((node) => {
    if (!hasPositionAttribute(node.geometry)) return;
    if (node.geometry.boundingBox == null) {
      node.geometry.computeBoundingBox();
    }
    if (!node.geometry.boundingBox || node.geometry.boundingBox.isEmpty()) return;
    const worldBox = node.geometry.boundingBox.clone().applyMatrix4(node.matrixWorld);
    _box.union(worldBox);
  });
  if (_box.isEmpty()) {
    _box.setFromCenterAndSize(object.position, new THREE.Vector3(2, 2, 2));
  }
  _box.getCenter(_center);
  _box.getSize(_size);
  const radius = Math.max(_size.length() * 0.6, 2);

  if (!controls?.current) return;

  new TWEEN.Tween(controls.current.target)
    .to({ x: _center.x, y: _center.y, z: _center.z }, 1100)
    .easing(TWEEN.Easing.Cubic.Out)
    .start();

  new TWEEN.Tween(camera.position)
    .to({ x: _center.x, y: _center.y + radius * 0.35, z: _center.z + radius }, 1000)
    .easing(TWEEN.Easing.Cubic.Out)
    .start();
}

const SidebarScene = ({ objectDetailsObject }) => {
  const ref = useRef();

  return (
    <Canvas shadows gl={{ antialias: true }} camera={{ position: [0, 500, 0.0], near: 0.5, far: 10000, fov: 35 }}>
      <OrbitControls ref={ref} enableZoom={true} enablePan={false} />
      <SidebarObject objectDetailsObject={objectDetailsObject} controls={ref} />
    </Canvas>
  );
};

export default SidebarScene;

const SidebarObject = ({ objectDetailsObject, controls }) => {
  const { camera, scene } = useThree();
  const previewRef = useRef();
  const instanceId = useClickStore((s) => s.instanceId);

  useEffect(() => {
    const previous = previewRef.current;
    if (previous) {
      scene.remove(previous);
      previous.traverse((node) => {
        node.geometry?.dispose?.();
        if (Array.isArray(node.material)) node.material.forEach((mat) => mat.dispose?.());
        else node.material?.dispose?.();
      });
      previewRef.current = null;
    }

    if (!scene.getObjectByName('SidebarKeyLight')) {
      const directionalLight1 = new THREE.DirectionalLight(0xffffcc, 1.2);
      directionalLight1.name = 'SidebarKeyLight';
      directionalLight1.position.set(-800, 0, -2000);
      scene.add(directionalLight1);

      const directionalLight = new THREE.DirectionalLight(0xffccff, 1.2);
      directionalLight.name = 'SidebarFillLight';
      directionalLight.position.set(800, 0, 2000);
      scene.add(directionalLight);

      const ambientLight = new THREE.AmbientLight(0xffffff, 1.1);
      ambientLight.name = 'SidebarAmbient';
      scene.add(ambientLight);
    }

    if (!objectDetailsObject || !controls?.current) return undefined;

    let preview = null;
    try {
      preview = previewFromPick(objectDetailsObject, instanceId);
    } catch (err) {
      console.warn('[SidebarScene] could not build preview', err);
    }
    if (!preview) return undefined;

    scene.add(preview);
    previewRef.current = preview;
    try {
      fitCameraToObject(camera, controls, preview);
    } catch (err) {
      console.warn('[SidebarScene] could not frame preview', err);
    }

    return () => {
      if (previewRef.current) {
        scene.remove(previewRef.current);
        previewRef.current.traverse((node) => {
          node.geometry?.dispose?.();
          if (Array.isArray(node.material)) node.material.forEach((mat) => mat.dispose?.());
          else node.material?.dispose?.();
        });
        previewRef.current = null;
      }
    };
  }, [objectDetailsObject, instanceId, camera, scene, controls]);

  return null;
};
